import { ArrowRight, BarChart2, Activity, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import SearchBar from "./SearchBar";

const Hero = () => {
  return (
    <section className="relative pt-28 pb-24 overflow-hidden">
      {/* Background elements with animation */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-benchr-500/5 dark:bg-benchr-500/10 rounded-full blur-3xl opacity-70 float" style={{ animationDuration: '8s' }} />
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-benchr-400/10 rounded-full blur-2xl opacity-60 float" style={{ animationDuration: '12s', animationDelay: '0.5s' }} />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-benchr-600/5 dark:bg-benchr-600/10 rounded-full blur-3xl opacity-60 float" style={{ animationDuration: '10s', animationDelay: '1s' }} />
      </div>

      <div className="container max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
          {/* Eyebrow with delay */}
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-benchr-50 dark:bg-benchr-900/40 border border-benchr-200/70 dark:border-benchr-700/30 mb-6 fade-in" style={{ animationDelay: '300ms' }}>
            <div className="w-2 h-2 rounded-full bg-green-500 mr-2 pulse-soft"></div>
            <span className="text-sm font-medium text-benchr-700 dark:text-benchr-200">
              Now launched — Benchr Intelligence 2.0
            </span>
          </div>

          {/* Main H1 headline with staggered animation */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-benchr-900 dark:text-white mb-6 tracking-tight leading-tight fade-in" style={{ animationDelay: '500ms' }}>
            E-commerce <span className="text-gradient">Competitive Intelligence</span> & Market Analysis Platform
          </h1>

          {/* Subheadline with delay */}
          <p className="text-xl text-benchr-600 dark:text-benchr-200 max-w-2xl mb-10 leading-relaxed fade-in" style={{ animationDelay: '700ms' }}>
            Access comprehensive product data, real-time price monitoring, and competitive market analysis from any global e-commerce website.
          </p>

          {/* Search Bar - Replacing Book a Demo button */}
          <div className="mb-10 w-full fade-in" style={{ animationDelay: "900ms" }}>
            <SearchBar 
              onSearch={(query) => {
                console.log("Searching for:", query);
                // Implement search functionality here
              }}
            />
          </div>
          
          {/* Credit Card Message with animation */}
          <div className="flex items-center justify-center mb-8 text-benchr-500 dark:text-benchr-300 fade-in" style={{ animationDelay: '1100ms' }}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span className="text-sm">No credit card required</span>
          </div>

          {/* Added title above feature cards */}
          <h2 className="text-2xl md:text-3xl font-bold text-benchr-900 dark:text-white mb-8 text-gradient fade-in" style={{ animationDelay: '1200ms' }}>
            Global E-commerce Market Coverage
          </h2>

          {/* Feature Cards with H3 headings and staggered animations */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl w-full">
            {[
              {
                icon: <BarChart2 className="text-white" size={24} />,
                iconBg: "from-benchr-600 to-benchr-700",
                title: "Product Monitoring",
                description: "Access comprehensive product data from any e-commerce site worldwide. Monitor competitors' catalogs and track inventory changes.",
                delay: "1300ms",
                image: "/lovable-uploads/92e9ae59-4c3c-42e8-8269-f0f0189eb0b7.png"
              },
              {
                icon: <Activity className="text-white" size={24} />,
                iconBg: "from-slate-600 to-slate-700",
                title: "Price Intelligence",
                description: "Get real-time pricing data from any e-commerce site globally. Track pricing strategies and promotional patterns.",
                delay: "1500ms",
                image: ""
              },
              {
                icon: <RefreshCw className="text-white" size={24} />,
                iconBg: "from-gray-600 to-gray-700",
                title: "Competitor Analysis",
                description: "Access comprehensive market data from any e-commerce platform worldwide. Analyze competition patterns and consumer behavior.",
                delay: "1700ms",
                image: ""
              },
            ].map((feature, index) => (
              <div
                key={index}
                className={cn(
                  "group relative p-6 bg-white/60 dark:bg-benchr-900/40 backdrop-blur-sm rounded-xl border border-benchr-100/80 dark:border-benchr-800/60 shadow-lg hover:shadow-xl transition-all duration-500 ease-in-out overflow-hidden fade-in",
                  "hover:scale-[1.03] hover:border-benchr-300/60 dark:hover:border-benchr-700",
                  "flex flex-col items-center text-center"
                )}
                style={{ animationDelay: feature.delay }}
              >
                {/* Decorative background */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-benchr-50/30 to-transparent dark:from-benchr-700/20 dark:to-transparent rounded-full blur-xl opacity-70 float" style={{ animationDuration: '15s' }} />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-tr from-benchr-100/30 to-transparent dark:from-benchr-800/20 dark:to-transparent rounded-full blur-xl opacity-70 float" style={{ animationDuration: '12s', animationDelay: '0.5s' }} />
                
                {/* Card content */}
                <div className="relative z-10 flex flex-col items-center">
                  {/* Icon with gradient and animation */}
                  <div className={`rounded-full p-3 bg-gradient-to-r ${feature.iconBg} w-14 h-14 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500 ease-in-out shadow-lg`}>
                    {feature.icon}
                  </div>
                  
                  <h3 className="text-xl font-bold text-benchr-800 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  
                  <p className="text-benchr-600 dark:text-benchr-300">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
